import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Truck, 
  Search,
  Edit,
  Trash2,
  ChevronRight,
  AlertTriangle,
  Wrench,
  Download,
  FileSpreadsheet,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import VehicleFormDialog from '../components/VehicleFormDialog';
import BulkVehicleDialog from '../components/BulkVehicleDialog';
import ImportResultDialog from '../components/ImportResultDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VEHICLE_TYPES = [
  { value: 'bakkie', label: 'Bakkie' },
  { value: 'truck', label: 'Truck' },
  { value: 'van', label: 'Van' },
  { value: 'car', label: 'Car' },
  { value: 'bus', label: 'Bus' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'trailer', label: 'Trailer' },
  { value: 'forklift', label: 'Forklift' },
  { value: 'excavator', label: 'Excavator' },
  { value: 'generator', label: 'Generator' },
  { value: 'other', label: 'Other' },
];

const USAGE_UNITS = [
  { value: 'kilometer', label: 'Kilometers (km)' },
  { value: 'hours', label: 'Hours (hrs)' },
];

const SERVICE_INTERVALS = [
  { value: 5000, label: '5,000 km' },
  { value: 10000, label: '10,000 km' },
  { value: 15000, label: '15,000 km' },
  { value: 20000, label: '20,000 km' },
];

const SERVICE_INTERVALS_HOURS = [
  { value: 500, label: '500 hours' },
  { value: 1000, label: '1,000 hours' },
];

const emptyVehicle = {
  name: '',
  registration: '',
  vehicle_type: '',
  usage_unit: 'kilometer',
  make: '',
  model: '',
  year: '',
  vin: '',
  engine_number: '',
  current_odometer: '',
  current_hours: '',
  last_service_date: null,
  last_service_odometer: '',
  last_service_hours: '',
  service_interval_km: 5000,
  service_interval_hours: 500,
  next_service_expected_date: null,
  license_expiry: null,
  license_renewal_cost: '',
  include_in_fleet_avg: true,
  assigned_driver_id: ''
};

const VehiclesPage = () => {
  const { canEdit } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSoldArchive, setShowSoldArchive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formData, setFormData] = useState({ ...emptyVehicle });
  const [bulkVehicles, setBulkVehicles] = useState([{ ...emptyVehicle, _key: 0 }]);
  const [bulkKeyCounter, setBulkKeyCounter] = useState(1);
  const [saving, setSaving] = useState(false);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const handleRecalculateServices = async () => {
    setRecalculating(true);
    try {
      const r = await api.recalculateServices();
      toast.success(`Service schedule refreshed for ${r.updated} vehicle${r.updated === 1 ? '' : 's'}`, {
        description: [
          r.no_service_entry ? `${r.no_service_entry} without a service entry` : null,
          r.kept_newer_manual ? `${r.kept_newer_manual} kept (newer manual service date)` : null,
        ].filter(Boolean).join(' · ') || undefined,
      });
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate services');
    } finally {
      setRecalculating(false);
    }
  };
  const [importResultOpen, setImportResultOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [drivers, setDrivers] = useState([]);
  useEffect(() => {
    api.getDrivers().then(setDrivers).catch(() => setDrivers([]));
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const data = await api.getVehicles();
      setVehicles(data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const handleDownloadTemplate = async (populated) => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/vehicles/export/excel?populated=${populated}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fname = populated
        ? `vehicles_export_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
        : 'vehicle_import_template.xlsx';
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(populated ? 'Vehicles exported' : 'Template downloaded');
    } catch {
      toast.error('Failed to download file');
    } finally {
      setDownloading(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      toast.error('Please choose an .xlsx file');
      e.target.value = '';
      return;
    }
    setImporting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(
        `${API_URL}/api/vehicles/import/excel`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(response.data);
      setImportResultOpen(true);
      await fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({ ...emptyVehicle });
    setSelectedVehicle(null);
  };

  const handleOpenDialog = (vehicle = null) => {
    if (vehicle) {
      setSelectedVehicle(vehicle);
      setFormData({
        name: vehicle.name,
        registration: vehicle.registration,
        vehicle_type: vehicle.vehicle_type,
        usage_unit: vehicle.usage_unit || 'kilometer',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year?.toString() || '',
        vin: vehicle.vin || '',
        engine_number: vehicle.engine_number || '',
        current_odometer: vehicle.current_odometer?.toString() || '',
        current_hours: vehicle.current_hours?.toString() || '',
        last_service_date: vehicle.last_service_date || '',
        last_service_odometer: vehicle.last_service_odometer?.toString() || '',
        last_service_hours: vehicle.last_service_hours?.toString() || '',
        service_interval_km: vehicle.service_interval_km || 5000,
        service_interval_hours: vehicle.service_interval_hours || 500,
        next_service_expected_date: vehicle.next_service_expected_date || '',
        license_expiry: vehicle.license_expiry || '',
        license_renewal_cost: vehicle.license_renewal_cost != null ? String(vehicle.license_renewal_cost) : '',
        include_in_fleet_avg: vehicle.include_in_fleet_avg !== false,
        assigned_driver_id: vehicle.assigned_driver_id || ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.registration || !formData.vehicle_type) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        registration: formData.registration,
        vehicle_type: formData.vehicle_type,
        usage_unit: formData.usage_unit || 'kilometer',
        make: formData.make || null,
        model: formData.model || null,
        year: formData.year ? parseInt(formData.year) : null,
        vin: formData.vin || null,
        engine_number: formData.engine_number || null,
        current_odometer: formData.current_odometer ? parseFloat(formData.current_odometer) : null,
        current_hours: formData.current_hours ? parseFloat(formData.current_hours) : null,
        last_service_date: formData.last_service_date || null,
        last_service_odometer: formData.last_service_odometer ? parseFloat(formData.last_service_odometer) : null,
        last_service_hours: formData.last_service_hours ? parseFloat(formData.last_service_hours) : null,
        service_interval_km: formData.service_interval_km || 5000,
        service_interval_hours: formData.service_interval_hours || 500,
        next_service_expected_date: formData.next_service_expected_date || null,
        license_expiry: formData.license_expiry || null,
        license_renewal_cost: formData.license_renewal_cost === '' || formData.license_renewal_cost == null
          ? null
          : parseFloat(formData.license_renewal_cost),
        include_in_fleet_avg: formData.include_in_fleet_avg !== false,
        assigned_driver_id: formData.assigned_driver_id || null
      };

      if (selectedVehicle) {
        await api.updateVehicle(selectedVehicle.id, payload);
        toast.success('Vehicle updated successfully');
      } else {
        await api.createVehicle(payload);
        toast.success('Vehicle added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all vehicles
    for (let i = 0; i < bulkVehicles.length; i++) {
      const v = bulkVehicles[i];
      if (!v.name || !v.registration || !v.vehicle_type) {
        toast.error(`Please fill in required fields for vehicle ${i + 1}`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        vehicles: bulkVehicles.map(v => ({
          name: v.name,
          registration: v.registration,
          vehicle_type: v.vehicle_type,
          make: v.make || null,
          model: v.model || null,
          year: v.year ? parseInt(v.year) : null,
          vin: v.vin || null,
          engine_number: v.engine_number || null,
          current_odometer: v.current_odometer ? parseFloat(v.current_odometer) : null,
          last_service_date: v.last_service_date ? format(v.last_service_date, 'yyyy-MM-dd') : null,
          last_service_odometer: v.last_service_odometer ? parseFloat(v.last_service_odometer) : null,
          service_interval_km: v.service_interval_km || 5000,
          license_expiry: v.license_expiry ? format(v.license_expiry, 'yyyy-MM-dd') : null
        }))
      };

      await api.createVehiclesBulk(payload);
      toast.success(`${bulkVehicles.length} vehicle(s) added successfully`);
      setBulkDialogOpen(false);
      setBulkVehicles([{ ...emptyVehicle }]);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vehicles');
    } finally {
      setSaving(false);
    }
  };

  const addBulkVehicle = () => {
    setBulkVehicles([...bulkVehicles, { ...emptyVehicle, _key: bulkKeyCounter }]);
    setBulkKeyCounter(c => c + 1);
  };

  const removeBulkVehicle = (index) => {
    if (bulkVehicles.length > 1) {
      setBulkVehicles(bulkVehicles.filter((_, i) => i !== index));
    }
  };

  const updateBulkVehicle = (index, field, value) => {
    const updated = [...bulkVehicles];
    updated[index] = { ...updated[index], [field]: value };
    setBulkVehicles(updated);
  };

  const handleDelete = async () => {
    if (!selectedVehicle) return;
    
    try {
      await api.deleteVehicle(selectedVehicle.id);
      toast.success('Vehicle deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedVehicle(null);
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const handleToggleFleetAvg = async (vehicle, nextIncluded) => {
    // Optimistic update
    setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, include_in_fleet_avg: nextIncluded } : v));
    try {
      await api.updateVehicle(vehicle.id, { include_in_fleet_avg: nextIncluded });
      toast.success(nextIncluded
        ? `${vehicle.name} included in fleet Km/L average`
        : `${vehicle.name} excluded from fleet Km/L average`
      );
    } catch (error) {
      // Revert on failure
      setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, include_in_fleet_avg: !nextIncluded } : v));
      toast.error('Failed to update vehicle');
    }
  };

  const filteredVehicles = vehicles.filter(v => 
    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.registration.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // Split into active + sold so sold vehicles live in a separate collapsed
  // archive section at the bottom of the page.
  const activeVehicles = filteredVehicles.filter(v => !v.is_sold);
  const soldVehicles = filteredVehicles.filter(v => v.is_sold);

  const serviceDueCount = activeVehicles.filter(v => v.service_due).length;

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-200 rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Vehicles
          </h1>
          <p className="text-slate-500 mt-1">Manage your fleet vehicles</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={importInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFile}
              className="hidden"
              data-testid="import-vehicles-input"
            />
            {canEdit() && (
              <Button
                variant="outline"
                onClick={handleRecalculateServices}
                disabled={recalculating}
                title="Refresh last/next service for every vehicle from its latest service entry"
                data-testid="recalculate-services-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
                {recalculating ? 'Recalculating...' : 'Recalculate Services'}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleDownloadTemplate(true)}
              disabled={downloading}
              data-testid="download-vehicles-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              data-testid="upload-vehicles-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {importing ? 'Uploading…' : 'Upload Excel'}
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setBulkVehicles([{ ...emptyVehicle }]);
                setBulkDialogOpen(true);
              }}
              data-testid="add-multiple-vehicles-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Multiple
            </Button>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-gray-700 hover:bg-gray-600 text-white btn-active"
              data-testid="add-vehicle-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Vehicle
            </Button>
          </div>
          <button
            type="button"
            onClick={() => handleDownloadTemplate(false)}
            disabled={downloading}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="download-vehicle-blank-template-link"
          >
            Download blank template
          </button>
        </div>
      </div>

      {/* Service Due Alert */}
      {serviceDueCount > 0 && (
        <Card className="border-gray-300 bg-gray-100">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-gray-800">
                {serviceDueCount} vehicle{serviceDueCount > 1 ? 's' : ''} due for service
              </p>
              <p className="text-sm text-gray-600">
                Check the vehicles marked with a service reminder badge
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search vehicles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-vehicles-input"
        />
      </div>

      {/* Vehicles List */}
      {filteredVehicles.length > 0 ? (
        <>
        <p className="text-xs text-slate-500 -mb-1">
          Tick the box next to a vehicle to <span className="font-medium">include it in the fleet Km/L average</span>. Untick private / non-company vehicles so they don&apos;t skew the group average.
        </p>
        <div className="space-y-3">
          {activeVehicles.map((vehicle) => (
            <Card key={vehicle.id} className={`card-hover ${vehicle.service_due ? 'border-gray-400' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center gap-2 mr-2 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                    title={vehicle.include_in_fleet_avg !== false
                      ? 'Included in fleet Km/L average — untick to exclude (e.g., private vehicles)'
                      : 'Excluded from fleet Km/L average — tick to include'}
                  >
                    <Checkbox
                      id={`fleet-avg-${vehicle.id}`}
                      checked={vehicle.include_in_fleet_avg !== false}
                      onCheckedChange={(checked) => handleToggleFleetAvg(vehicle, checked === true)}
                      data-testid={`include-fleet-avg-${vehicle.id}`}
                      aria-label="Include in fleet Km/L average"
                    />
                  </div>
                  <Link 
                    to={`/vehicles/${vehicle.id}`}
                    className="flex items-center gap-4 flex-1 min-w-0"
                    data-testid={`vehicle-row-${vehicle.id}`}
                  >
                    <div className={`w-12 h-12 rounded-md flex items-center justify-center shrink-0 ${
                      vehicle.service_due ? 'bg-gray-200' : 'bg-slate-100'
                    }`}>
                      <Truck className={`w-6 h-6 ${vehicle.service_due ? 'text-gray-700' : 'text-slate-600'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-slate-900 truncate">
                          {vehicle.name}
                        </h3>
                        {vehicle.is_sold && (
                          <Badge variant="secondary" className="bg-slate-200 text-slate-700 text-xs" data-testid={`sold-badge-${vehicle.id}`}>
                            SOLD · {vehicle.sold_date}
                          </Badge>
                        )}
                        {!vehicle.is_sold && vehicle.service_due && (
                          <Badge variant="destructive" className="bg-gray-600 text-white text-xs">
                            <Wrench className="w-3 h-3 mr-1" />
                            Service Due
                          </Badge>
                        )}
                        {vehicle.include_in_fleet_avg === false && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200"
                            title="Excluded from fleet average — meter reading not tracked"
                            data-testid={`no-meter-tracking-badge-${vehicle.id}`}
                          >
                            no meter tracking
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 flex-wrap">
                        <span className="font-data">{vehicle.registration}</span>
                        <span className="capitalize">{vehicle.vehicle_type}</span>
                        {vehicle.make && vehicle.model && (
                          <span>{vehicle.make} {vehicle.model}</span>
                        )}
                        <span className={vehicle.assigned_driver_name ? 'text-slate-700' : 'text-slate-400 italic'} data-testid={`vehicle-driver-${vehicle.id}`}>
                          {vehicle.assigned_driver_name ? `Driver: ${vehicle.assigned_driver_name}` : 'No driver'}
                        </span>
                        {vehicle.usage_unit === 'hours' && vehicle.current_hours ? (
                          <span className="font-data">{vehicle.current_hours.toLocaleString()} hrs</span>
                        ) : vehicle.current_odometer && (
                          <span className="font-data">{vehicle.current_odometer.toLocaleString()} km</span>
                        )}
                      </div>
                      {(vehicle.next_service_km || vehicle.next_service_hours) && (
                        <p className="text-xs text-slate-500 mt-1 font-medium" data-testid={`vehicle-next-service-${vehicle.id}`}>
                          Next service at {vehicle.next_service_km
                            ? `${vehicle.next_service_km.toLocaleString()} km`
                            : `${vehicle.next_service_hours.toLocaleString()} hrs`}
                          {vehicle.next_service_expected_date && ` (~${vehicle.next_service_expected_date})`}
                          {vehicle.last_service_date && ` • Last serviced: ${vehicle.last_service_date}`}
                        </p>
                      )}
                      {!vehicle.next_service_km && !vehicle.next_service_hours && vehicle.next_service_expected_date && (() => {
                        const today = new Date(); today.setHours(0,0,0,0);
                        const nextSvc = new Date(vehicle.next_service_expected_date);
                        const daysRemaining = Math.round((nextSvc - today) / 86400000);
                        const isOverdue = daysRemaining < 0;
                        const isDueSoon = daysRemaining >= 0 && daysRemaining <= 30;
                        const cls = isOverdue ? 'text-rose-700 font-bold' : isDueSoon ? 'text-amber-700 font-bold' : 'text-slate-500 font-medium';
                        const status = isOverdue ? `OVERDUE by ${Math.abs(daysRemaining)} days` : `in ${daysRemaining} days`;
                        return (
                          <p className={`text-xs mt-1 ${cls}`} data-testid={`trailer-service-reminder-${vehicle.id}`}>
                            Next service: {vehicle.next_service_expected_date} ({status})
                            {vehicle.last_service_date && ` • Last: ${vehicle.last_service_date}`}
                          </p>
                        );
                      })()}
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(vehicle)}
                      data-testid={`edit-vehicle-${vehicle.id}`}
                    >
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedVehicle(vehicle);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-vehicle-${vehicle.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Link to={`/vehicles/${vehicle.id}`}>
                      <Button variant="ghost" size="icon">
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Sold vehicles archive — collapsed by default to keep the active list clean. */}
        {soldVehicles.length > 0 && (
          <div className="mt-6 border-t border-slate-200 pt-4" data-testid="sold-archive-section">
            <button
              type="button"
              onClick={() => setShowSoldArchive(v => !v)}
              className="w-full flex items-center justify-between text-left group"
              data-testid="sold-archive-toggle"
            >
              <div>
                <h3 className="font-heading font-semibold text-slate-700 group-hover:text-slate-900">
                  Sold vehicles · <span className="font-data text-slate-500">{soldVehicles.length}</span>
                </h3>
                <p className="text-xs text-slate-500">
                  Archived — kept for historical reports, excluded from forecasts, alerts and fleet averages.
                </p>
              </div>
              <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showSoldArchive ? 'rotate-90' : ''}`} />
            </button>

            {showSoldArchive && (
              <div className="space-y-2 mt-3">
                {soldVehicles.map((vehicle) => (
                  <Card key={vehicle.id} className="bg-slate-50 border-slate-200" data-testid={`sold-archive-row-${vehicle.id}`}>
                    <CardContent className="p-3">
                      <Link to={`/vehicles/${vehicle.id}`} className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md bg-slate-200 flex items-center justify-center shrink-0">
                          <Truck className="w-5 h-5 text-slate-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-heading font-semibold text-slate-700 truncate">{vehicle.name}</h4>
                            <Badge variant="secondary" className="bg-slate-200 text-slate-700 text-xs">
                              SOLD · {vehicle.sold_date}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
                            <span className="font-data">{vehicle.registration}</span>
                            <span className="capitalize">{vehicle.vehicle_type}</span>
                            {vehicle.sold_price != null && (
                              <span>Sold for <strong className="text-slate-700">R {Number(vehicle.sold_price).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</strong></span>
                            )}
                            {vehicle.sold_buyer && <span>→ {vehicle.sold_buyer}</span>}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
        </>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <Truck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">
              {searchTerm ? 'No vehicles found' : 'No vehicles yet'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchTerm 
                ? 'Try adjusting your search term'
                : 'Add your first vehicle to start tracking costs'
              }
            </p>
            {!searchTerm && (
              <div className="flex items-center justify-center gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setBulkVehicles([{ ...emptyVehicle }]);
                    setBulkDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Multiple
                </Button>
                <Button 
                  onClick={() => handleOpenDialog()}
                  className="bg-gray-700 hover:bg-gray-600 text-white"
                  data-testid="add-first-vehicle-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Vehicle
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Single Vehicle Dialog */}
      <VehicleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        formData={formData}
        setFormData={setFormData}
        selectedVehicle={selectedVehicle}
        saving={saving}
        onSubmit={handleSubmit}
        drivers={drivers}
      />

      {/* Add Multiple Vehicles Dialog */}
      <BulkVehicleDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        bulkVehicles={bulkVehicles}
        saving={saving}
        onSubmit={handleBulkSubmit}
        onAdd={addBulkVehicle}
        onRemove={removeBulkVehicle}
        onUpdate={updateBulkVehicle}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedVehicle?.name}&quot;? 
              This will also delete all associated cost entries. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportResultDialog
        open={importResultOpen}
        onOpenChange={setImportResultOpen}
        result={importResult}
        matchedByLabel="Registration"
        testIdPrefix="vehicle-import"
      />
    </div>
  );
};

export default VehiclesPage;
